import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Modal, Spin, Divider, Collapse, Table, Tag, Typography } from "antd";
import axios from "axios";
import {
  GET_GEO_ALL,
  HEADERS_EXPORT_BEN,
  POST_LAWSUIT_DETAIL, // ✅ เพิ่ม API endpoint
} from "../../../API/apiUrls";

// ========================================
// Main Component
// ========================================

const DetailAsset = ({ open, close, dataDefault }) => {
  // States
  const [loading, setLoading] = useState(false);
  const [dataAsset, setDataAsset] = useState([]); // ✅ เพิ่ม state
  const [geoRawData, setGeoRawData] = useState(null);

  // ========================================
  // Load Geo Data
  // ========================================

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const loadGeoData = async () => {
      try {
        const res = await axios.get(GET_GEO_ALL, {
          headers: HEADERS_EXPORT_BEN,
          signal: controller.signal,
        });

        if (isMounted) {
          setGeoRawData(res.data);
          console.log("✅ geoRawData loaded");
        }
      } catch (error) {
        if (error.name !== "CanceledError" && isMounted) {
          console.error("❌ Error loading geo data:", error);
        }
      }
    };

    loadGeoData();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  // ========================================
  // ✅ Load Asset Data
  // ========================================

  useEffect(() => {
    if (!open || !dataDefault) return;

    const loadAssetData = async () => {
      setLoading(true);
      try {
        const response = await axios.post(
          POST_LAWSUIT_DETAIL,
          {
            CONTNO: dataDefault.CONTNO,
            loanId: dataDefault.loanId,
            lawsuitId: dataDefault.lawsuitId,
          },
          {
            headers: HEADERS_EXPORT_BEN,
          },
        );

        if (response?.data?.asset) {
          setDataAsset(response.data.asset);
          console.log("✅ Asset data loaded:", response.data.asset);
        }
      } catch (error) {
        console.error("❌ Error loading asset data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadAssetData();
  }, [open, dataDefault]);

  // ========================================
  // Geo Helper Functions
  // ========================================

  const getProvinceName = useCallback(
    (code) => {
      if (!geoRawData?.province || !code) return "-";
      const province = geoRawData.province.find(
        (p) => String(p.provCode) === String(code),
      );
      return province?.provDesc || code;
    },
    [geoRawData],
  );

  const getDistrictName = useCallback(
    (provinceCode, districtCode) => {
      if (!geoRawData?.district || !provinceCode || !districtCode) return "-";
      const district = geoRawData.district.find(
        (d) =>
          String(d.provCode) === String(provinceCode) &&
          String(d.distCode) === String(districtCode),
      );
      return district?.distDesc || districtCode;
    },
    [geoRawData],
  );

  const getSubDistrictName = useCallback(
    (provinceCode, districtCode, subDistrictCode) => {
      if (
        !geoRawData?.subDistrict ||
        !provinceCode ||
        !districtCode ||
        !subDistrictCode
      )
        return "-";
      const subDistrict = geoRawData.subDistrict.find(
        (s) =>
          String(s.provCode) === String(provinceCode) &&
          String(s.distCode) === String(districtCode) &&
          String(s.subDistCode) === String(subDistrictCode),
      );
      return subDistrict?.subDistDesc || subDistrictCode;
    },
    [geoRawData],
  );

  // ========================================
  // Table Columns
  // ========================================

  const assetColumns = useMemo(
    () => [
      {
        title: "ประเภททรัพย์",
        dataIndex: "typeLand",
        key: "typeLand",
        width: 120,
        render: (type) => {
          const typeMap = {
            1: "โฉนด",
            2: "นส.3ก.",
            3: "ห้องชุด",
            4: "อาคารชุด",
            5: "นภ.1",
            6: "อื่น ๆ",
          };
          return typeMap[type] || "-";
        },
      },
      {
        title: "เลขที่ดิน",
        dataIndex: "numberLand",
        key: "numberLand",
        width: 150,
      },
      {
        title: "จังหวัด",
        dataIndex: "province",
        key: "province",
        width: 150,
        render: (code) => getProvinceName(code),
      },
      {
        title: "อำเภอ",
        dataIndex: "district",
        key: "district",
        width: 150,
        render: (code, record) => getDistrictName(record.province, code),
      },
      {
        title: "ตำบล",
        dataIndex: "subdistrict",
        key: "subdistrict",
        width: 150,
        render: (code, record) =>
          getSubDistrictName(record.province, record.district, code),
      },
    ],
    [getProvinceName, getDistrictName, getSubDistrictName],
  );

  // ========================================
  // ✅ Render รายละเอียดทรัพย์
  // ========================================

  const renderAssetDetail = useMemo(() => {
    if (!dataAsset || dataAsset.length === 0) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            border: "1px solid #e8e8e8",
            borderRadius: "8px",
            color: "#999",
          }}
        >
          ไม่มีข้อมูลทรัพย์สิน
        </div>
      );
    }

    const assetItems = dataAsset.map((person, index) => {
      const hasAsset = person.assetLand && person.assetLand.length > 0;
      const activeAssets = person.assetLand?.filter((a) => a.active) || [];

      return {
        key: String(index),
        label: (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <span>
              <strong>
                {person.SNAM}
                {person.NAME1} {person.NAME2}
              </strong>
              {person.garno > 0 && (
                <Tag color="orange" style={{ marginLeft: 8 }}>
                  {person.relation || `คนค้ำที่ ${person.garno}`}
                </Tag>
              )}
              {person.garno === 0 && (
                <Tag color="blue" style={{ marginLeft: 8 }}>
                  {person.relation || "ผู้เช่าซื้อ"}
                </Tag>
              )}
            </span>
            <span>
              <Tag color={hasAsset ? "green" : "red"}>
                {hasAsset
                  ? `พบทรัพย์ ${activeAssets.length} รายการ`
                  : "ไม่พบทรัพย์"}
              </Tag>
            </span>
          </div>
        ),
        children: (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Typography.Text>
                <strong>เลขบัตร:</strong> {person.CUSCOD || "-"}
              </Typography.Text>
              {person.relation && (
                <>
                  {" | "}
                  <Typography.Text>
                    <strong>ความสัมพันธ์:</strong> {person.relation}
                  </Typography.Text>
                </>
              )}
            </div>

            {hasAsset ? (
              <>
                <Divider orientation="left" style={{ margin: "12px 0" }}>
                  รายการทรัพย์สิน
                </Divider>
                <Table
                  dataSource={person.assetLand}
                  columns={assetColumns}
                  rowKey="landId"
                  pagination={false}
                  size="small"
                  bordered
                  scroll={{ x: 800 }}
                />
              </>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "20px",
                  color: "#999",
                  backgroundColor: "#fafafa",
                  borderRadius: "4px",
                }}
              >
                ไม่มีข้อมูลทรัพย์สิน
              </div>
            )}
          </div>
        ),
      };
    });

    return (
      <>
        <Divider>รายละเอียดทรัพย์</Divider>
        <Collapse items={assetItems} accordion />
      </>
    );
  }, [dataAsset, assetColumns]);

  // ========================================
  // Main Render
  // ========================================

  return (
    <Modal
      title={`รายละเอียดทรัพย์ - ${dataDefault?.CONTNO || ""}`}
      open={open}
      onCancel={close}
      width={1200}
      footer={null}
      destroyOnHidden
    >
      <Spin spinning={loading} size="large" tip="กำลังโหลดข้อมูล...">
        {renderAssetDetail}
      </Spin>
    </Modal>
  );
};

export default DetailAsset;
